/**
 * InsertSlideIndicator - Modern hover-reveal insert button
 *
 * Shows a subtle "+" indicator between slides that expands on hover.
 * Used in both OutlineWorkspacePanel and VideoWorkspaceFilmstrip.
 */

import React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InsertSlideIndicatorProps {
  /** Called when the insert button is clicked */
  onInsert: () => void;
  /** Orientation - affects styling */
  orientation?: 'vertical' | 'horizontal';
  /** Whether the indicator is disabled */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

export const InsertSlideIndicator: React.FC<InsertSlideIndicatorProps> = ({
  onInsert,
  orientation = 'vertical',
  disabled = false,
  className,
}) => {
  const isVertical = orientation === 'vertical';

  if (disabled) return null;

  return (
    <div
      className={cn(
        "group/insert relative flex items-center justify-center",
        isVertical ? "h-5 w-full my-0" : "w-4 h-full mx-0",
        className
      )}
    >
      {/* Hover line indicator */}
      <div
        className={cn(
          "absolute transition-all duration-200",
          isVertical
            ? "left-4 right-4 h-0.5 bg-primary/0 group-hover/insert:bg-primary/30"
            : "top-4 bottom-4 w-0.5 bg-primary/0 group-hover/insert:bg-primary/30"
        )}
      />

      {/* Insert button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onInsert();
        }}
        className={cn(
          "relative z-10 flex items-center justify-center",
          "w-6 h-6 rounded-full",
          "bg-gray-100 dark:bg-zinc-800",
          "border border-gray-200 dark:border-zinc-700",
          "text-gray-400 dark:text-zinc-500",
          "opacity-0 scale-75",
          "group-hover/insert:opacity-100 group-hover/insert:scale-100",
          "hover:bg-primary hover:border-primary hover:text-primary-foreground",
          "dark:hover:bg-primary/90 dark:hover:border-primary",
          "transition-all duration-200",
          "shadow-sm hover:shadow-md"
        )}
        title="Insert slide here"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/**
 * Compact version for filmstrip view
 */
export const InsertSlideIndicatorCompact: React.FC<InsertSlideIndicatorProps> = ({
  onInsert,
  orientation = 'horizontal',
  disabled = false,
  className,
}) => {
  const isVertical = orientation === 'vertical';

  if (disabled) return null;

  return (
    <div
      className={cn(
        "group/insert relative flex items-center justify-center",
        isVertical ? "h-2 w-full" : "w-2 h-full flex-shrink-0",
        className
      )}
    >
      {/* Insert button - smaller for filmstrip */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onInsert();
        }}
        className={cn(
          "relative z-10 flex items-center justify-center",
          "w-5 h-5 rounded-full",
          "bg-white/80 dark:bg-zinc-900/80",
          "border border-gray-200 dark:border-zinc-700",
          "text-gray-400 dark:text-zinc-500",
          "opacity-0 scale-50",
          "group-hover/insert:opacity-100 group-hover/insert:scale-100",
          "hover:bg-primary hover:border-primary hover:text-primary-foreground",
          "dark:hover:bg-primary/90 dark:hover:border-primary",
          "transition-all duration-150",
          "shadow-sm"
        )}
        title="Insert slide"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
};
